import PhiDisclaimerFooter from '@brands/Components/PhiDisclaimerFooter/PhiDisclaimerFooter';
import Sidenav from '@brands/Dashboard/Sidenav/Sidenav';
import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import { isUnifiedStaffCaseIntakeRole } from '@brands/Utils/reviewProfileBasePath';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectStaffCaseCreation } from '@brands/store/selectors/staffCaseCreation';
import { selectMyProfileSidebarState } from '@brands/store/selectors/myProfileSidebarState';
import { selectSidebarState } from '@brands/store/selectors/sidebarState';
import { selectVideoCallCredentials } from '@brands/store/selectors/videoCallCredentials';
import { toggleSidebar } from '@brands/store/slices/sidebarStateSlice';
import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';

import { useWindowSize } from '../../hooks';
import { UserRoleName } from '../../services/identity/types/UserProfile';
import styles from './styles.module.scss';

interface ProfileContainerProps {
  children: React.ReactNode;
  containerStyle?: object;
  childClassName?: string;
  childrenStyle?: { [key: string]: string };
  isVisibleOnMobile?: boolean;
  isProfileCard?: boolean;
  consultationPage?: boolean;
  scrollableDashboard?: boolean;
  patientDashboard?: boolean;
  isSidenavVisible?: boolean;
  isProvider?: boolean;
  isCareAdvisorPage?: boolean;
  isIntakeProcess?: boolean;
  grayBackground?: boolean;
  /** Suppresses the footer this Container would otherwise render after `children` — for callers (e.g. a
   * two-column layout) that place their own PhiDisclaimerFooter inside one column instead of below both. */
  hidePhiDisclaimerFooter?: boolean;
}

const Container: FC<ProfileContainerProps> = ({
  children,
  containerStyle = {},
  childClassName = '',
  childrenStyle = {},
  isVisibleOnMobile = true,
  isProfileCard = false,
  consultationPage = false,
  scrollableDashboard = false,
  patientDashboard = false,
  isSidenavVisible = true,
  isProvider = false,
  isCareAdvisorPage = false,
  isIntakeProcess = false,
  grayBackground = false,
  hidePhiDisclaimerFooter = false,
}): JSX.Element => {
  const { isCollapsed } = useAppSelector(selectSidebarState);
  const { userInfo } = useAppSelector(selectAuth);
  const { isMyProfileSidebarCollapsed } = useAppSelector(selectMyProfileSidebarState);
  const { is_visible: isVideoVisible, mobileModalMode, mobileModalHeight } = useAppSelector(selectVideoCallCredentials);
  const { isStaffIntake } = useAppSelector(selectStaffCaseCreation);
  const dispatch = useAppDispatch();
  const screenSize = useWindowSize();
  const isMobile = screenSize.width < 768;
  const isMobileVideoOpen = isMobile && isVideoVisible;
  const staffUnifiedIntake =
    isStaffIntake && userInfo?.role?.name != null && isUnifiedStaffCaseIntakeRole(userInfo.role.name);
  const effectiveSidenavVisible = staffUnifiedIntake ? false : isSidenavVisible;
  const effectiveIsIntakeProcess = staffUnifiedIntake ? true : isIntakeProcess;

  const triggerSidebar = (): void => {
    if (!isCollapsed && isMobile) {
      dispatch(toggleSidebar());
    }
  };

  const mobileVideoTopPadding = useMemo(() => {
    if (!isMobile || !isVideoVisible) return 0;

    if (mobileModalMode === 'fullscreen') {
      return 0;
    }

    if (mobileModalMode === 'collapsed' || mobileModalMode === 'expanded') {
      return mobileModalHeight;
    }

    return 0;
  }, [isMobile, isVideoVisible, mobileModalMode, mobileModalHeight]);
  const toastAnchorRef = useRef<HTMLDivElement | null>(null);

  const resetToastAnchor = useCallback((): void => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    root.style.setProperty('--d1-toast-content-center', '50vw');
    root.style.setProperty('--d1-toast-content-left', '0px');
    root.style.setProperty('--d1-toast-content-width', '100vw');
    root.style.setProperty('--d1-toast-available-width', 'calc(100vw - 32px)');
  }, []);

  const updateToastAnchor = useCallback((): void => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const node = toastAnchorRef.current;

    if (!node) {
      resetToastAnchor();
      return;
    }

    const rect = node.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(node);
    const paddingLeft = Number.parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = Number.parseFloat(computedStyle.paddingRight) || 0;
    const left = Math.min(Math.max(rect.left + paddingLeft, 0), window.innerWidth);
    const contentWidth = Math.max(0, rect.width - paddingLeft - paddingRight);
    const width = Math.max(0, Math.min(contentWidth, window.innerWidth - left));
    const center = left + width / 2;
    const availableWidth = Math.max(0, width - 32);
    const root = document.documentElement;

    root.style.setProperty('--d1-toast-content-center', `${center}px`);
    root.style.setProperty('--d1-toast-content-left', `${left}px`);
    root.style.setProperty('--d1-toast-content-width', `${width}px`);
    root.style.setProperty('--d1-toast-available-width', `${availableWidth}px`);
  }, [resetToastAnchor]);

  const setToastAnchorRef = useCallback(
    (node: HTMLDivElement | null): void => {
      toastAnchorRef.current = node;

      if (node && typeof window !== 'undefined') {
        window.requestAnimationFrame(updateToastAnchor);
      }
    },
    [updateToastAnchor],
  );

  useEffect(() => {
    updateToastAnchor();

    if (typeof window === 'undefined') return undefined;

    const handleResize = (): void => updateToastAnchor();
    const transitionTimer = window.setTimeout(updateToastAnchor, 650);

    window.addEventListener('resize', handleResize);

    return () => {
      window.clearTimeout(transitionTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, [
    effectiveIsIntakeProcess,
    effectiveSidenavVisible,
    isCollapsed,
    isMobile,
    isMyProfileSidebarCollapsed,
    mobileVideoTopPadding,
    screenSize.width,
    updateToastAnchor,
  ]);

  useEffect(() => resetToastAnchor, [resetToastAnchor]);

  const isDashboardRole =
    userInfo?.role?.name === UserRoleName.Patient ||
    userInfo?.role?.name === UserRoleName.Provider ||
    userInfo?.role?.name === UserRoleName.CareAdvisor ||
    userInfo?.role?.name === UserRoleName.Collector ||
    userInfo?.role?.name === UserRoleName.Admin ||
    userInfo?.role?.name === UserRoleName.SuperAdmin ||
    userInfo?.role?.name === UserRoleName.OrganizationAdmin;
  const shouldApplySidebarOffsets = isSidenavVisible && isDashboardRole;

  const isAllowedRole =
    userInfo?.role?.name === UserRoleName.Patient ||
    userInfo?.role?.name === UserRoleName.Provider ||
    userInfo?.role?.name === UserRoleName.CareAdvisor ||
    userInfo?.role?.name === UserRoleName.Collector;

  return (
    <div
      className={`${styles.containerClass} ${
        userInfo?.role?.name !== UserRoleName.Patient && styles.backgroundColor
      } ${childClassName} `}
      style={{
        ...(grayBackground ? { backgroundColor: '#F5F5F5' } : {}),
        ...(isMobile && { bottom: '84px', top: '8px' }),
        ...(isMobile && isVideoVisible && mobileModalMode !== 'fullscreen'
          ? { marginTop: `${mobileVideoTopPadding}px` }
          : {}),
        height:
          isMobile && isVideoVisible && mobileModalMode !== 'fullscreen'
            ? `calc(100vh - 64px - ${mobileVideoTopPadding}px)`
            : 'calc(100vh - 64px)',
        ...containerStyle,
      }}
      onClick={() => triggerSidebar()}
    >
      {effectiveSidenavVisible && <Sidenav isVisibleOnMobile={isVisibleOnMobile} isProfileCard={isProfileCard} />}
      {patientDashboard && (
        <div
          ref={setToastAnchorRef}
          id="patientDashboardPosition"
          className={`${styles.patientDashboardPosition} ${
            shouldApplySidebarOffsets && (isCollapsed || isIntakeProcess)
              ? styles.collapsed
              : shouldApplySidebarOffsets
              ? styles.expanded
              : ''
          } ${
            shouldApplySidebarOffsets && isMyProfileSidebarCollapsed
              ? styles.collapsedMyProfile
              : shouldApplySidebarOffsets
              ? styles.expandedMyProfile
              : ''
          } ${isMobileVideoOpen ? styles.mobileVideoContentOffset : ''}`}
          style={{
            height:
              isMobile && isVideoVisible && mobileModalMode !== 'fullscreen'
                ? `calc(100vh - 64px - ${mobileVideoTopPadding}px)`
                : 'calc(100vh - 64px)',
            ...childrenStyle,
          }}
        >
          <div className={styles.patientDashboardContent}>
            {children}
            {!hidePhiDisclaimerFooter && <PhiDisclaimerFooter />}
          </div>
        </div>
      )}
      {consultationPage && (
        <div
          ref={setToastAnchorRef}
          className={`${styles.childrenPositionTop} ${
            (isCollapsed || effectiveIsIntakeProcess) && isAllowedRole
              ? styles.collapsed
              : isAllowedRole
              ? styles.expanded
              : ''
          } ${
            isMyProfileSidebarCollapsed && isAllowedRole
              ? styles.collapsedMyProfile
              : isAllowedRole
              ? styles.expandedMyProfile
              : ''
          } ${isMobileVideoOpen ? styles.mobileVideoContentOffset : ''}`}
          style={{
            height:
              isMobile && isVideoVisible && mobileModalMode !== 'fullscreen'
                ? `calc(100vh - 64px - ${mobileVideoTopPadding}px)`
                : 'calc(100vh - 64px)',
            ...childrenStyle,
          }}
        >
          <div className={styles.footerAnchor}>
            {children}
            <PhiDisclaimerFooter />
          </div>
        </div>
      )}
      {scrollableDashboard && (
        <div
          ref={setToastAnchorRef}
          className={`${styles.childrenScrollableDiv} ${
            (isCollapsed || effectiveIsIntakeProcess) && isAllowedRole
              ? styles.collapsed
              : isAllowedRole
              ? styles.expanded
              : ''
          } ${
            isMyProfileSidebarCollapsed && isAllowedRole
              ? styles.collapsedMyProfile
              : isAllowedRole
              ? styles.expandedMyProfile
              : ''
          } ${isMobileVideoOpen ? styles.mobileVideoContentOffset : ''}`}
          style={{
            height:
              isMobile && isVideoVisible && mobileModalMode !== 'fullscreen'
                ? `calc(100vh - 64px - ${mobileVideoTopPadding}px)`
                : 'calc(100vh - 64px)',
            ...childrenStyle,
          }}
        >
          <div className={styles.footerAnchor}>
            {children}
            <PhiDisclaimerFooter />
          </div>
        </div>
      )}

      {!consultationPage && !patientDashboard && !scrollableDashboard && !isProvider && (
        <div
          ref={setToastAnchorRef}
          className={`${styles.childrenCunsomPosition} ${
            isCollapsed
              ? styles.collapsed
              : effectiveIsIntakeProcess
              ? styles.collapsedWithNoNav
              : styles.expanded
          } ${
            isMyProfileSidebarCollapsed
              ? styles.collapsedMyProfile
              : styles.expandedMyProfile
          } ${isMobileVideoOpen ? styles.mobileVideoContentOffset : ''}`}
          style={{
            height:
              isMobile && isVideoVisible && mobileModalMode !== 'fullscreen'
                ? `calc(100vh - 64px - ${mobileVideoTopPadding}px)`
                : 'calc(100vh - 64px)',
            ...childrenStyle,
          }}
        >
          <div className={styles.footerAnchor}>
            {children}
            <PhiDisclaimerFooter />
          </div>
        </div>
      )}
      {isProvider && (
        <div
          ref={setToastAnchorRef}
          className={`${styles.childrenProviderCunsomPosition}
          ${isCareAdvisorPage && styles.childrenCACunsomPosition}
          ${
            (isCollapsed || effectiveIsIntakeProcess) && isAllowedRole
              ? styles.collapsed
              : isAllowedRole
              ? styles.expanded
              : ''
          } ${
            isMyProfileSidebarCollapsed && isAllowedRole
              ? styles.collapsedMyProfile
              : isAllowedRole
              ? styles.expandedMyProfile
              : ''
          } ${isMobileVideoOpen ? styles.mobileVideoContentOffset : ''}`}
          style={{
            ...childrenStyle,
            height:
              isMobile && isVideoVisible ? `calc(100vh - 64px - ${mobileVideoTopPadding}px)` : 'calc(100vh - 64px)',
          }}
        >
          <div className={styles.footerAnchor}>
            {children}
            {!hidePhiDisclaimerFooter && <PhiDisclaimerFooter />}
          </div>
        </div>
      )}
    </div>
  );
};

export default Container;
